// Client-side analytics helper. Every event fans out to three sinks so each tool
// sees the same interactions under the same names:
//   1. our first-party, cookieless /api/analytics collector (always);
//   2. GTM's dataLayer, so the web + server-side (sGTM) containers can fire GA4 /
//      Google Ads / Meta / TikTok tags on landing-page interactions;
//   3. the consent-gated PostHog client (once loaded), for A/B experiment goals.
// Safe to call from anywhere in the browser, before or after GTM/PostHog load.

import posthog from "posthog-js";
import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  TrackArgs,
} from "@/lib/events";

type Props = Record<string, unknown>;

function posthogCapture(name: string, props?: Props) {
  if ((posthog as unknown as { __loaded?: boolean }).__loaded === true) {
    posthog.capture(name, props);
  }
}

// Push an event onto GTM's dataLayer. The container processes the queue when it
// loads, so this is safe to call before the GTM script is ready. Tags that touch
// cookies/PII stay gated by Consent Mode inside GTM — pushing here does not bypass
// consent, it only makes the interaction available to the container.
function pushToDataLayer(event: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

function collectUtm(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "fbclid",
  ]) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }
  return utm;
}

function send(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const body = JSON.stringify(payload);
  const url = "/api/analytics";
  // Prefer sendBeacon so the request survives page unloads / navigations.
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
  } else {
    fetch(url, {
      method: "POST",
      body,
      headers: { "Content-Type": "application/json" },
      keepalive: true,
    }).catch(() => {});
  }
}

// A client-side route change, announced to GTM so the container can fire a GA4
// page_view for it. The site is a Next.js app: after the first load, navigation
// never reloads the document, so the GA4 configuration tag - which fires once, on
// Initialization - never sends another page_view. Every internal page was
// therefore missing from GA4 while Meta, which the container drives off the
// built-in History Change trigger, saw them.
//
// History Change is not enough on its own: it fires the moment the URL changes,
// before Next has swapped the document title, so a page_view built from it
// carries the previous page's name. This runs from the route effect instead,
// after the new route has rendered, and hands the container the title outright.
//
// Deliberately not in the typed event catalog: it is a GTM-only signal. Routing
// it through track() would duplicate the first-party pageview and push a
// synthetic event into PostHog, which captures pageviews itself.
//
// Returns a canceller, which the caller must wire to its effect cleanup. The
// frame's worth of delay is what makes two navigations inside one frame - a
// double-clicked prefetched link, a held-down back button - able to collide:
// both callbacks would read the same final location, dropping the first route
// and reporting the last one twice. Cancelling on re-run leaves exactly one
// push, for the route the visitor actually ended up on.
export function pushVirtualPageView(): (() => void) | undefined {
  if (typeof window === "undefined") return;
  const frame = requestAnimationFrame(() => {
    pushToDataLayer({
      event: "virtual_page_view",
      page_path: `${window.location.pathname}${window.location.search}`,
      page_location: window.location.href,
      page_title: document.title,
    });
  });
  return () => cancelAnimationFrame(frame);
}

export function trackPageview(path: string, locale?: string) {
  send({
    type: "pageview",
    path,
    locale,
    referrer: document.referrer || undefined,
    utm: collectUtm(),
    screen: `${window.screen.width}x${window.screen.height}`,
  });
}

// Send an analytics event. Generic over the event catalog in events.ts: the name
// must be a declared event and the payload must match its shape (events with an
// empty payload take no second argument). Fans out to all three sinks.
export function track<E extends AnalyticsEventName>(name: E, ...args: TrackArgs<E>) {
  if (typeof window === "undefined") return;
  const props = args[0] as AnalyticsEventMap[E] | undefined;
  send({
    type: "event",
    name,
    path: window.location.pathname,
    utm: collectUtm(),
    props,
  });
  posthogCapture(name, props as Props | undefined);
  // GTM custom-event triggers key off the top-level `event` field; flatten props
  // alongside it so dataLayer variables can read them directly.
  pushToDataLayer({ event: name, ...props });
}

// Build an app URL carrying the `link_id` attribution param the app reads on
// arrival. Use for the anchor `href` so the destination and the reported click
// agree on the link id.
export function ctaHref(href: string, linkId?: string): string {
  if (!linkId) return href;
  return `${href}${href.includes("?") ? "&" : "?"}link_id=${linkId}`;
}

// The anchor id GTM keys CTA reporting off. The container's {{CJS - CTA Click ID}}
// variable resolves `el.closest('a[id^="cta_"]')`, and trigger 8 turns that into
// `clicked_on_sign_up` - a GA4 key event, and the conversion the server-side
// Google Ads tag fires on. An app-bound CTA without a `cta_` id is therefore
// invisible to paid reporting, which is why this is derived rather than opt-in.
export function ctaElementId(linkId?: string): string | undefined {
  return linkId ? `cta_${linkId}` : undefined;
}

// A login link is not a sign-up CTA. Trigger 8 matches the `cta_` prefix blindly,
// so a login anchor wearing one reports every returning sign-in as
// `clicked_on_sign_up` - a GA4 key event and a paid conversion. That is not
// hypothetical: it is why the Jul'26 landing A/B test was unreadable. This site
// tagged all 11 app-bound anchors including both logins, the Framer control tagged
// 2 of 5 and neither login, so the variant arm looked like it turned 22% more
// clicks into sign-up intent when it was counting a different thing. Login anchors
// get their own prefix: the click is still reported as `cta_click` to first-party
// and PostHog, it just stops minting a conversion.
export function loginElementId(linkId: string): string {
  return `login_${linkId}`;
}

// Outbound CTA → the app. Reports one consistent `cta_click` shape to all sinks
// (first-party, GTM dataLayer, PostHog). `cta_href` is the base destination plus
// `link_id` only: AttributionForwarder stamps the click ids and UTMs onto the
// anchor itself after mount, so the URL actually navigated to carries more than
// this. That is deliberate - the reported href stays a stable, low-cardinality
// value to group on, and the ad identifiers reach the platforms that need them
// through the anchor and the first-touch cookie rather than through event props.
export function trackCtaClick(
  linkId: string | undefined,
  href: string,
  extra?: Props,
) {
  track("cta_click", { link_id: linkId ?? null, cta_href: ctaHref(href, linkId), ...extra });
}

// Page-engagement helpers. Thin wrappers over track() so the event names stay
// consistent everywhere and reach all three sinks (first-party, GTM, PostHog).

// A page section scrolled into view. Fired once per section per page load by
// PageEngagement. `section` is a stable, locale-independent id (e.g. "hero").
export function trackSectionView(section: string) {
  track("section_viewed", { section });
}

// A scroll-depth milestone (25 / 50 / 75 / 100) was reached, once each per load.
export function trackScrollDepth(depth: number) {
  track("scroll_depth", { depth });
}

// A FAQ / accordion item was opened (not fired on close).
export function trackFaqOpen(index: number, question: string) {
  track("faq_opened", { index, question });
}

// Dispatched by PostHogProvider the moment posthog.init() finishes or capturing
// is opted back in. Anything that needs PostHog at page load has to wait for
// this: PostHog does not exist until the visitor answers the consent banner,
// which is always after mount.
export const POSTHOG_READY_EVENT = "tvt:posthog-ready";

// Landing-page A/B split exposure, first-party + GTM legs.
//
// Deliberately does NOT touch PostHog - see registerExperimentArm. Splitting the
// two is what fixes the bug where the arm never reached PostHog at all: this
// runs on mount, PostHog initialises only once OneTrust grants C0002, and that
// is always later, so the PostHog leg of a single combined call silently no-op'd
// every single time. Every event on the arm then went unsegmented, which is the
// whole reason the component exists.
export function trackExperimentExposure(experiment: string, variant: string) {
  if (typeof window === "undefined") return;
  const props: AnalyticsEventMap["experiment_exposure"] = { experiment, variant };
  send({
    type: "event",
    name: "experiment_exposure",
    path: window.location.pathname,
    utm: collectUtm(),
    props,
  });
  pushToDataLayer({ event: "experiment_exposure", ...props });
}

// The PostHog leg of the exposure: register the arm as a super property so every
// subsequent event is segmented by it, then record the exposure itself.
//
// Returns false when PostHog is not loaded, so the caller can wait for
// POSTHOG_READY_EVENT and try again. Consent-safe by construction: PostHog is
// only ever loaded once analytics consent is granted, so a false here is the
// no-consent case and a true is the granted one.
export function registerExperimentArm(experiment: string, variant: string): boolean {
  if ((posthog as unknown as { __loaded?: boolean }).__loaded !== true) return false;
  posthog.register({ ab_variant: variant });
  posthog.capture("experiment_exposure", { experiment, variant });
  return true;
}
